import classNames from "classnames";
import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { FiInfo } from "react-icons/fi";
import "./index.scss";

interface InfoTooltipProps {
  children: ReactNode;
  content?: ReactNode;
  showOnDisabled?: boolean;
  open?: boolean;
  className?: string;
  id?: string;
  onOpenChange?: ((open: boolean) => void) | undefined;
}

interface TooltipBubbleProps {
  open: boolean;
  id?: string | undefined;
  className?: string | undefined;
  children: ReactNode;
}

const VIEWPORT_PADDING = 12;

function TooltipBubble({ open, id, className, children }: TooltipBubbleProps) {
  const tooltipRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return;

    function clampTooltip() {
      const el = tooltipRef.current;
      if (!el) return;

      el.style.setProperty("--tooltip-shift", "0px");

      const rect = el.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      let shift = 0;

      if (rect.right > viewportWidth - VIEWPORT_PADDING) {
        shift = viewportWidth - VIEWPORT_PADDING - rect.right;
      }

      if (rect.left + shift < VIEWPORT_PADDING) {
        shift = VIEWPORT_PADDING - rect.left;
      }

      el.style.setProperty("--tooltip-shift", `${shift}px`);
    }

    clampTooltip();
    window.addEventListener("resize", clampTooltip);
    window.addEventListener("orientationchange", clampTooltip);

    return () => {
      window.removeEventListener("resize", clampTooltip);
      window.removeEventListener("orientationchange", clampTooltip);
    };
  }, [open, children]);

  if (!open) return null;

  return (
    <div
      ref={tooltipRef}
      id={id}
      className={classNames("search-toggle-tooltip", className)}
      role="tooltip"
    >
      <span className="icon" aria-hidden="true">
        <FiInfo />
      </span>
      <p className="text">{children}</p>
    </div>
  );
}

export function InfoTooltip({
  children,
  content,
  showOnDisabled,
  open,
  className,
  id,
  onOpenChange,
}: InfoTooltipProps) {
  const generatedId = useId();
  const tooltipId = id ?? generatedId;
  const isWrapMode = content != null;

  const [internalOpen, setInternalOpen] = useState(false);

  const wrapperRef = useRef<HTMLSpanElement>(null);
  const tooltipOpenRef = useRef(false);
  const pointerTypeRef = useRef<string>("mouse");
  const prevOpenRef = useRef<boolean | null>(null);

  const trigger = isWrapMode ? Children.only(children) : null;
  const triggerDisabled = Boolean(
    isValidElement(trigger) &&
    (trigger.props as { disabled?: boolean }).disabled,
  );
  const interactionsEnabled =
    isWrapMode && (!showOnDisabled || triggerDisabled);
  const isOpen = isWrapMode
    ? Boolean(open ?? internalOpen) && (!showOnDisabled || triggerDisabled)
    : Boolean(open);
  const message = isWrapMode ? content : children;

  useEffect(() => {
    tooltipOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (prevOpenRef.current === isOpen) return;
    prevOpenRef.current = isOpen;
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (!interactionsEnabled) {
      setInternalOpen(false);
    }
  }, [interactionsEnabled]);

  useEffect(() => {
    if (!isWrapMode || !interactionsEnabled) return;

    function handleOutside(e: MouseEvent) {
      const target = e.target as Node;
      const isInside = wrapperRef.current?.contains(target);

      if (!isInside && tooltipOpenRef.current) {
        setInternalOpen(false);
      }
    }

    document.addEventListener("mousedown", handleOutside);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
    };
  }, [isWrapMode, interactionsEnabled]);

  if (!isWrapMode) {
    return (
      <TooltipBubble open={isOpen} id={id} className={className}>
        {message}
      </TooltipBubble>
    );
  }

  const describedTrigger = isValidElement(trigger)
    ? cloneElement(
        trigger as ReactElement<{ "aria-describedby"?: string }>,
        isOpen ? { "aria-describedby": tooltipId } : {},
      )
    : trigger;

  return (
    <span
      ref={wrapperRef}
      className={classNames("info-tooltip-wrap", {
        "is-disabled-trigger": triggerDisabled,
      })}
      onPointerDown={(e) => {
        pointerTypeRef.current = e.pointerType;
      }}
      onPointerEnter={(e) => {
        if (!interactionsEnabled) return;
        if (e.pointerType === "mouse") setInternalOpen(true);
      }}
      onPointerLeave={(e) => {
        if (e.pointerType === "mouse") setInternalOpen(false);
      }}
      onClick={(e) => {
        if (!interactionsEnabled) return;
        e.preventDefault();
        e.stopPropagation();
        if (pointerTypeRef.current === "mouse") return;
        setInternalOpen((current) => !current);
      }}
    >
      {describedTrigger}
      <TooltipBubble open={isOpen} id={tooltipId} className={className}>
        {message}
      </TooltipBubble>
    </span>
  );
}
